import React from 'react';
import PropTypes from 'prop-types';
import { SECURITY_CONTROL_RULES } from './optimizerRuleRegistry';
import { customerSecurityText } from '../../utils/securityIdentityPresentation';
import Select from '../ui/Select';
import Tooltip from '../ui/Tooltip';
import { cx } from '../ui/classNames';

const OPTIONS = Object.freeze([
    { value: 'free', label: 'Free' },
    ...SECURITY_CONTROL_RULES.map((rule) => ({
        value: rule.control,
        label: rule.optionLabel,
    })),
]);

// In a dense table row the control reads as text with a chevron, as the old
// borderless select did: 28px tall, 0.72rem text, no fill, and no border
// until it is hovered or focused. A set control is bold; Free is muted.
const CELL_SELECT_CLASS_NAME = cx(
    'w-full [&>select]:!h-7 [&>select]:text-[0.72rem] [&>select]:!bg-transparent',
    '[&>select:not(:hover):not(:focus)]:border-transparent',
);
const VALUE_CLASS_NAMES = Object.freeze({
    free: '[&>select]:text-ui-muted',
    set: '[&>select]:font-bold',
});

// One security's trading control. Only checked names are run, so an unchecked
// name's control is kept but cannot be edited. The table ignores clicks and
// keys that start on the control, so it never touches row selection.
export default function SecurityControlSelect({
    cogniveId,
    ticker = null,
    value,
    checked,
    disabled = false,
    onChange,
}) {
    const select = (
        <Select
            value={value}
            disabled={disabled || !checked}
            onChange={(event) => onChange(cogniveId, event.target.value)}
            aria-label={`Trading control for ${customerSecurityText(ticker, { cognive_id: cogniveId }) || 'this security'}`}
            className={cx(CELL_SELECT_CLASS_NAME, VALUE_CLASS_NAMES[value === 'free' ? 'free' : 'set'])}
        >
            {OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
            ))}
        </Select>
    );
    if (checked) return select;
    // A disabled <select> fires no mouse events, so the tooltip sits on a
    // wrapper.
    return (
        <Tooltip content="Check this name to set a trading control">
            <span className="flex w-full">{select}</span>
        </Tooltip>
    );
}

SecurityControlSelect.propTypes = {
    cogniveId: PropTypes.string.isRequired,
    ticker: PropTypes.string,
    value: PropTypes.oneOf(OPTIONS.map((option) => option.value)).isRequired,
    checked: PropTypes.bool.isRequired,
    disabled: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
};
